import DtoolsReviewProposalPreview, {
  type DtoolsProposalCoverInput,
} from "./DtoolsReviewProposalPreview";
import type { ReactNode } from "react";
import type { DtoolsOptionSectionActionsProps } from "@/components/sales/dtools/DtoolsOptionSectionActions";
import type { DtoolsDisplaySettings, DtoolsPortalPaymentTerm } from "@shared/dtoolsDisplaySettings";
import type { DtoolsJsonParseResult } from "@shared/dtoolsSiJsonImport";
import { enrichDtoolsReviewBuildContext } from "@shared/dtoolsReviewNavigation";
import type { DtoolsProposalPresentation, ProposalPresentationHeaderInput } from "@shared/dtoolsProposalPresentation";
import type { DtoolsReviewFooter, DtoolsReviewSystem } from "@shared/dtoolsStagingTransform";
import type { DtoolsSystemContent } from "@shared/dtoolsProposalContent";
import type { DtoolsSystemClassificationsMap, DtoolsTabModelVersion } from "@shared/dtoolsSystemClassification";
import type { ProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import type { DtoolsPdfPackageMeta } from "@shared/dtoolsPdfPackage";
import type { VoSystemOverviewAnnotation } from "@shared/variationOrderSystemOverview";

type Props = {
  systems: DtoolsReviewSystem[];
  footer: DtoolsReviewFooter;
  parseResult?: DtoolsJsonParseResult | null;
  paymentTerms?: DtoolsPortalPaymentTerm[];
  displaySettings?: DtoolsDisplaySettings;
  systemContentMap?: Record<string, DtoolsSystemContent>;
  presentation?: DtoolsProposalPresentation;
  headerPresentation?: ProposalPresentationHeaderInput;
  /** Customer-facing preview in Step 3; staff note only when staff. */
  audience?: "customer" | "staff";
  /** Staff sandbox: message when Cleo description/hover is missing. */
  cleoDescriptionPendingMessage?: string;
  /** Scroll-spy section ids for each option (Step 3). */
  optionSectionIds?: string[];
  navigationSystemKey?: string | null;
  navigationRequestToken?: number;
  showApprovalPlaceholder?: boolean;
  cover?: DtoolsProposalCoverInput | null;
  optionActions?: Omit<DtoolsOptionSectionActionsProps, "optionLabel">;
  contentLead?: ReactNode;
  tabModelVersion?: DtoolsTabModelVersion;
  systemClassifications?: DtoolsSystemClassificationsMap;
  customerPricing?: ProposalCustomerPricingDisplay | null;
  retailPricingJson?: {
    listTotal?: number | null;
    originalListPrice?: number | null;
    agreedPriceSgd?: number | null;
    agreedPrice?: number | null;
  } | null;
  totalAmount?: number | string | null;
  pdfPackageMeta?: DtoolsPdfPackageMeta;
  linkBExpiresAt?: string | Date | null;
  dualPreviewAccordion?: boolean;
  systemOverviewAnnotations?: Record<string, VoSystemOverviewAnnotation>;
};

export default function DtoolsReviewHierarchy({
  systems,
  footer,
  parseResult,
  paymentTerms = [],
  displaySettings = {},
  systemContentMap = {},
  presentation,
  headerPresentation,
  audience = "customer",
  cleoDescriptionPendingMessage,
  optionSectionIds,
  navigationSystemKey,
  navigationRequestToken,
  showApprovalPlaceholder = true,
  cover,
  optionActions,
  contentLead,
  tabModelVersion,
  systemClassifications,
  customerPricing,
  retailPricingJson,
  totalAmount,
  pdfPackageMeta,
  linkBExpiresAt,
  dualPreviewAccordion = false,
  systemOverviewAnnotations,
}: Props) {
  const reviewContext = enrichDtoolsReviewBuildContext(systems, {
    systemContentMap,
    presentation,
    systemClassifications,
    systemOverviewAnnotations,
  });

  return (
    <DtoolsReviewProposalPreview
      systems={systems}
      footer={footer}
      parseResult={parseResult}
      paymentTerms={paymentTerms}
      displaySettings={displaySettings}
      reviewContext={reviewContext}
      presentation={presentation}
      headerPresentation={headerPresentation}
      showApprovalPlaceholder={showApprovalPlaceholder}
      cover={cover}
      audience={audience}
      cleoDescriptionPendingMessage={cleoDescriptionPendingMessage}
      optionActions={optionActions ?? { isSandbox: true }}
      optionSectionIds={optionSectionIds}
      navigationSystemKey={navigationSystemKey}
      navigationRequestToken={navigationRequestToken}
      contentLead={contentLead}
      tabModelVersion={tabModelVersion}
      systemClassifications={systemClassifications}
      customerPricing={customerPricing}
      retailPricingJson={retailPricingJson}
      totalAmount={totalAmount}
      pdfPackageMeta={pdfPackageMeta}
      linkBExpiresAt={linkBExpiresAt}
      dualPreviewAccordion={dualPreviewAccordion}
    />
  );
}
